/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef, useEffect } from 'react'
import { Layout, Modal, message } from 'antd'
import { AntdProvider } from '@/components/AntdProvider'
import sensors from '../../utils/sensors'
import CONFIG from '../../constants/config'
import { renameUser, bunchInvite } from '../../actions/team'
import {
  fetchGroupInfo,
  fetchGroupDeparment,
  createDeparment,
  deleteDepartment,
  renameDepartment,
  setUsersDepartment,
  addUsersToDepartment,
  batchDeleteUsers
} from '../../actions/group'

import {
  DepartmentName,
  ActionsBar,
  SetDepartmentModal,
  AddMembersModal,
  NewUser
} from './components'

import DepartmentTree from './DepartmentTree'
import { setCurDepartment } from '../../slices/organizationSlice'
import { DepartmentMember } from '../../global'
import { capturePromiseException } from '../../utils/sentryUtils'
import { Status } from '../../utils/exceptionHelper'
import emptyImage from '../../images/department-empty.png'
import { useAppDispatch, useAppSelector, usePayment } from '../../app/hooks'
import dayjs from 'dayjs'
import MemberSearchResult from './MemberSearchResult'
import MemberTable from './MemberTable'
import { GlobalSearchBar } from './componentsV2'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

const Empty = () => {
  const payment = usePayment()
  return (
    <Content className="content empty">
      <img src={emptyImage} alt="" />
      <p>"部门管理"为团队超级会员权益，解锁"部门管理"，灵活管理员工与部门</p>
      <button onClick={() => payment.openSvip('webGroupDepartmentNum')}>
        开通超级会员
      </button>
    </Content>
  )
}

function Organization() {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.main.user)
  const groupId = useAppSelector((state) => state.group.groupId)
  const groupName = useAppSelector((state) => state.group.groupName)
  const organization = useAppSelector(
    (state) => state.organization.organization
  )
  const curDepartment = useAppSelector(
    (state) => state.organization.curDepartment
  ) as any

  const [setDepartmentModalVisible, setSetDepartmentModalVisible] = useState(
    false
  )
  const [addMembersModalVisible, setAddMembersModalVisible] = useState(false)
  const [selectedUserDepartmentIDs, setSelectedUserDepartmentIDs] = useState<
    string[]
  >([])
  const [selectedUsers, setSelectedUsers] = useState<DepartmentMember[]>([])
  const [searchKeywords, setSearchKeywords] = useState('')
  const [memberSearchValue, setMemberSearchValue] = useState('')

  const globalSearchRef = useRef<any>(null)

  useEffect(() => {
    dispatch(fetchGroupDeparment({ groupID: groupId }))
  }, [dispatch, groupId])

  const handleCreateDepartment = (name: string) => {
    return dispatch(
      createDeparment({
        groupID: groupId,
        parentDepartmentID: curDepartment.id,
        departmentName: name
      })
    )
      .then((data) => {
        if (data.status === 0) {
          dispatch(fetchGroupDeparment({ groupID: groupId }))
        } else if (data.status === Status.svipForbiden) {
          message.error('部门管理为团队"超级会员"权益，您暂时无法享受此权益')
        }
      })
      .catch(capturePromiseException)
  }

  const handleDeleteDepartment = () => {
    sensors.track('web_click_page_workgroup_groupcontacts', {
      clickItem: 'deleteDepartment',
      groupID: groupId
    })
    if (
      curDepartment &&
      curDepartment.children.length === 0 &&
      curDepartment.users.length === 0
    ) {
      dispatch(
        deleteDepartment({
          groupID: groupId,
          departmentID: curDepartment.id
        })
      )
        .then(() => {
          dispatch(fetchGroupDeparment({ groupID: groupId }))
          dispatch(setCurDepartment(organization))
        })
        .catch(capturePromiseException)
    } else {
      message.warning('请先转出部门内所有人再删除部门')
    }
  }

  const handleRenameDepartment = (departmentName) => {
    return dispatch(
      renameDepartment({
        groupID: groupId,
        departmentID: curDepartment.id,
        departmentName
      })
    )
      .then(() => {
        dispatch(fetchGroupDeparment({ groupID: groupId }))
      })
      .catch((err) => {
        dispatch(setCurDepartment(organization))
        capturePromiseException(err)
      })
  }

  const handleSetUsersDepartment = (departmentList) => {
    const doSetAction = () =>
      dispatch(
        setUsersDepartment({
          groupID: groupId,
          departmentUserList: selectedUserDepartmentIDs,
          departmentList: departmentList
        })
      )
        .then((res) => {
          if (res.status === -129) {
            message.error(`操作失败${res.msg}`)
            setSetDepartmentModalVisible(false)
            return
          }
          message.success('操作成功')
          dispatch(fetchGroupDeparment({ groupID: groupId }))
          setSetDepartmentModalVisible(false)
          setSelectedUserDepartmentIDs([])
          setSelectedUsers([])
        })
        .catch(capturePromiseException)

    // 要调整的成员中，如果有部门管理员，则提示
    if (
      curDepartment.users.some((user) => {
        return (
          selectedUserDepartmentIDs.includes(user.departmentUserID) &&
          user.roleType === CONFIG.memberRoleTypes.DEPARTMENT_ADMIN
        )
      })
    ) {
      return new Promise((resolve, reject) => {
        Modal.confirm({
          title: '提示',
          content: '部门管理员，转移部门后，将成为新部门管理员',
          onOk: () => {
            resolve(doSetAction())
          }
        })
      })
    }
    return doSetAction()
  }

  const handleAddUsersToDepartment = (departmentUserList, departmentList) => {
    sensors.track('web_click_page_workgroup_groupcontacts', {
      clickItem: 'addMember',
      groupID: groupId
    })
    return dispatch(
      addUsersToDepartment({
        groupID: groupId,
        departmentUserList: departmentUserList,
        departmentList: departmentList,
        destDepartment: curDepartment.id
      })
    )
      .then(() => {
        dispatch(fetchGroupDeparment({ groupID: groupId }))
        setAddMembersModalVisible(false)
        setSelectedUserDepartmentIDs([])
        setSelectedUsers([])
      })
      .catch(capturePromiseException)
  }

  const handleAddMember = (newMember: NewUser, res: any) => {
    return dispatch(
      bunchInvite({
        userID: user?.user_id,
        groupID: groupId,
        departmentID: curDepartment?.id,
        mobiles: [newMember],
        ticket: res.ticket,
        randStr: res.randstr
      })
    )
      .then((data) => {
        if (data.status === 0) {
          if (
            data.mobiles &&
            data.mobiles[0] &&
            data.mobiles[0].mobileStatus === 0
          ) {
            Modal.info({
              title: '邀请成功',
              content: '已发出短信邀请，对方同意后即可加入团队',
              okText: '知道了',
              onOk: () => {
                dispatch(fetchGroupDeparment({ groupID: groupId }))
              }
            })
          } else {
            switch (data.mobiles[0].mobileStatus) {
              case 4:
                Modal.warning({
                  title: '邀请失败',
                  content: '该成员已在团队中'
                })
                break
              case 7:
                Modal.warning({
                  title: '邀请失败',
                  content: '24小时内，请勿重复邀请'
                })
                break
              case -8:
                Modal.warning({
                  title: '邀请失败',
                  content: '短信发送失败'
                })
                break
              case -18:
                Modal.warning({
                  title: '邀请失败',
                  content: '手机号不正确'
                })
                break
              default:
                Modal.warning({
                  title: '邀请失败',
                  content: '请联系客服'
                })
                break
            }
          }
        } else {
          Modal.warning({
            title: '邀请失败',
            content: data.msg
          })
        }
      })
      .catch(capturePromiseException)
  }

  const handleSetDepartmentClick = () => {
    sensors.track('web_click_page_workgroup_groupcontacts', {
      clickItem: 'setDepartment',
      groupID: groupId
    })
    if (curDepartment?.id === '0' && curDepartment?.children.length === 0) {
      message.warning('请先去创建下级部门')
    } else if (selectedUserDepartmentIDs.length === 0) {
      message.warning('请选择要设置部门的员工')
    } else {
      setSetDepartmentModalVisible(true)
    }
  }

  const handleAddMembersClick = () => {
    if (curDepartment?.id === '0' && curDepartment?.children.length === 0) {
      message.warning('请创建下级部门')
    } else {
      setAddMembersModalVisible(true)
    }
  }

  const handleBatchDeleteClick = (userList = selectedUsers) => {
    if (userList.length === 0) {
      message.warning('请先选择要删除的成员')
    } else if (
      userList.some((selectedUser) => selectedUser.userID === user?.user_id) ||
      userList.some(
        (selectedUser) =>
          selectedUser.roleType === CONFIG.memberRoleTypes.MASTER_ADMIN
      )
    ) {
      message.warning('无法删除主管理员或当前操作人员')
    } else {
      return new Promise((resolve, reject) => {
        Modal.confirm({
          title: '是否将成员移除出本团队？',
          cancelText: '取消',
          okText: '移除',
          okType: 'danger',
          onOk: () =>
            dispatch(
              batchDeleteUsers({
                groupID: groupId,
                userList: userList.map((selectedUser) => selectedUser.userID)
              })
            )
              .then(() => {
                setSelectedUsers([])
                setSelectedUserDepartmentIDs([])
                dispatch(fetchGroupDeparment({ groupID: groupId }))
                resolve('')
              })
              .catch(capturePromiseException),
          onCancel: () => reject()
        })
      })
    }
    return Promise.reject()
  }

  const handleCurDepartmentChange = (curDepartment) => {
    dispatch(setCurDepartment(curDepartment))
    setSearchKeywords('')
    setSelectedUserDepartmentIDs([])
    setSelectedUsers([])
  }

  const dataSource: DepartmentMember[] = curDepartment
    ? curDepartment.users.filter((user: any) => {
        if (searchKeywords !== '') {
          return (
            (user.userStatus === 1 || user.userStatus === -2) &&
            (user.nickname.indexOf(searchKeywords) > -1 ||
              user.mobile.indexOf(searchKeywords) > -1)
          )
        } else {
          return user.userStatus === 1 || user.userStatus === -2
        }
      })
    : []

  const rowSelection = {
    selectedRowKeys: selectedUserDepartmentIDs,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedUserDepartmentIDs(selectedRowKeys)
      setSelectedUsers(selectedRows)
    }
  }

  return (
    <AntdProvider>
      <Layout className="Department">
        <PageHeader
          className="Header"
          ghost={true}
          title="成员/部门管理"
          extra={
            <GlobalSearchBar
              ref={globalSearchRef}
              onSearch={(value) => setMemberSearchValue(value)}
            />
          }
        />
        {memberSearchValue.trim() ? (
          <MemberSearchResult
            onBatchDeleteBtnClick={handleBatchDeleteClick}
            memberSearchValue={memberSearchValue}
            onCancel={() => {
              setMemberSearchValue('')
              if (globalSearchRef.current) {
                globalSearchRef.current.onClear()
              }
            }}
          />
        ) : (
          <Content className="content">
            <DepartmentTree onCurDepartmentChange={handleCurDepartmentChange} />
            <div className="member-table-container">
              <DepartmentName
                onDelete={handleDeleteDepartment}
                onRename={handleRenameDepartment}
                onNew={handleCreateDepartment}
              />
              <ActionsBar
                onSetBtnClick={handleSetDepartmentClick}
                onAddBtnClick={handleAddMembersClick}
                onBatchDeleteBtnClick={handleBatchDeleteClick}
                onSearch={(value) => {
                  sensors.track('web_click_page_workgroup_groupcontacts', {
                    clickItem: 'search',
                    groupID: groupId
                  })
                  setSearchKeywords(value)
                }}
                onAddMember={handleAddMember}
              />
              <MemberTable
                dataSource={dataSource}
                rowSelection={rowSelection}
                refreshData={() =>
                  dispatch(
                    fetchGroupDeparment({
                      groupID: groupId
                    })
                  )
                }
                handleDeleteClick={handleBatchDeleteClick}
              />
            </div>
            <SetDepartmentModal
              visible={setDepartmentModalVisible}
              onCancel={() => setSetDepartmentModalVisible(false)}
              onOk={handleSetUsersDepartment}
            />
            <AddMembersModal
              visible={addMembersModalVisible}
              onCancel={() => setAddMembersModalVisible(false)}
              onOk={handleAddUsersToDepartment}
            />
          </Content>
        )}
      </Layout>
    </AntdProvider>
  )
}

export default Organization
